import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { WorkoutExerciseSetDTO } from '@/types/workout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Check, ChevronLeft, ChevronRight, Info, Play } from 'lucide-react';

interface ExerciseViewProps {
  currentSet: WorkoutExerciseSetDTO;
  onComplete: (data?: {
    actual_reps?: number;
    actual_weight?: number;
    actual_duration_seconds?: number;
    actual_rpe?: number;
  }) => void;
  onSkip?: () => void;
  onSaveMetadata?: (
    setId: string,
    metadata: {
      reps?: number;
      duration_seconds?: number;
      weight?: number;
      rpe?: number;
    }
  ) => Promise<void>;
  setTimeRemaining?: number; // Remaining time from parent (calculated from backend)
  onStartSet?: () => void; // Callback to start the set
  isSetInProgress?: boolean; // Whether the set has been started
  onPrevious?: () => void; // Navigate to previous set
  onNext?: () => void; // Navigate to next set (when in history)
  isNavigatingHistory?: boolean; // Whether currently navigating through history
}

export function ExerciseView({
  currentSet,
  onComplete,
  onSkip,
  onSaveMetadata,
  setTimeRemaining,
  onStartSet,
  isSetInProgress = false,
  onPrevious,
  onNext,
  isNavigatingHistory = false,
}: ExerciseViewProps) {
  const exercise = currentSet.exercise.exercise;
  // Time-based if duration_seconds is defined and > 0
  const isTimeBased = currentSet.duration_seconds !== undefined && 
                      currentSet.duration_seconds > 0;
  const totalSets = currentSet.exercise.sets.length;
  
  const [editedReps, setEditedReps] = useState<number | undefined>(currentSet.reps);
  const [editedDuration, setEditedDuration] = useState<number | undefined>(currentSet.duration_seconds);
  const [editedWeight, setEditedWeight] = useState<number | undefined>(currentSet.weight);
  const [editedRpe, setEditedRpe] = useState<number | undefined>(currentSet.rpe);
  const [isSavingMetadata, setIsSavingMetadata] = useState(false);
  const [showExerciseInfo, setShowExerciseInfo] = useState(false);
  
  // Auto-populate edit fields when current set changes
  useEffect(() => {
    setEditedReps(currentSet.reps);
    setEditedDuration(currentSet.duration_seconds);
    setEditedWeight(currentSet.weight);
    setEditedRpe(currentSet.rpe);
  }, [currentSet.set_id]);
  
  // Check if any fields have been changed
  const hasChanges = 
    editedReps !== currentSet.reps ||
    editedDuration !== currentSet.duration_seconds ||
    editedWeight !== currentSet.weight ||
    editedRpe !== currentSet.rpe;
  
  const handleSaveMetadata = async () => {
    if (!onSaveMetadata || !hasChanges) return;
    
    setIsSavingMetadata(true);
    try {
      await onSaveMetadata(currentSet.set_id, {
        reps: editedReps,
        duration_seconds: editedDuration,
        weight: editedWeight,
        rpe: editedRpe,
      });
    } catch (error) {
      console.error('Failed to save metadata:', error);
    } finally {
      setIsSavingMetadata(false);
    }
  };

  // Helper function to format duration in MM:SS format
  const formatDuration = (totalSeconds: number) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Use time remaining from parent (calculated from backend) or fallback to duration
  const timeRemaining = setTimeRemaining !== undefined 
    ? setTimeRemaining 
    : (isTimeBased ? (currentSet.duration_seconds || 0) : 0);
  
  const minutes = Math.floor(timeRemaining / 60);
  const seconds = timeRemaining % 60;

  // Get target duration for time-based exercises
  const targetDuration = currentSet.duration_seconds || 0;

  // Check if this is part of a superset
  const isSuperset = currentSet.exercise.superset_group !== null && currentSet.exercise.superset_group !== undefined;

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-6">
      {isNavigatingHistory && (
        <div className="mb-4 px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
          Viewing previous set - changes won't be saved
        </div>
      )}
      <div className="text-center mb-8 w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-4 relative">
          <h2 className="text-4xl font-semibold text-neutral-900">{exercise.name}</h2>
          <div className="relative">
            <button
              className="p-1.5 rounded-full hover:bg-neutral-100 transition-colors"
              onClick={() => setShowExerciseInfo(!showExerciseInfo)}
              aria-label="Exercise information"
            >
              <Info className="h-5 w-5 text-neutral-500" />
            </button>
            {showExerciseInfo && (
              <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 z-50 w-72 rounded-lg border border-neutral-200 bg-white p-4 shadow-lg">
                <div className="space-y-3">
                  <h3 className="font-semibold text-neutral-900">{exercise.name}</h3>
                  {exercise.description && (
                    <div>
                      <p className="text-sm text-neutral-600">{exercise.description}</p>
                    </div>
                  )}
                  {exercise.muscle_group && (
                    <div>
                      <p className="text-xs font-medium text-neutral-500 uppercase mb-1">Muscle Group</p>
                      <p className="text-sm text-neutral-700">{exercise.muscle_group}</p>
                    </div>
                  )}
                  {exercise.equipment && (
                    <div>
                      <p className="text-xs font-medium text-neutral-500 uppercase mb-1">Equipment</p>
                      <p className="text-sm text-neutral-700">{exercise.equipment}</p>
                    </div>
                  )}
                </div>
                {/* Arrow */}
                <div className="absolute left-1/2 transform -translate-x-1/2 top-full w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-neutral-200"></div>
                <div className="absolute left-1/2 transform -translate-x-1/2 top-full mt-[-1px] w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-white"></div>
              </div>
            )}
          </div>
        </div>
        {showExerciseInfo && (
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowExerciseInfo(false)}
          />
        )}

        {isTimeBased ? (
          <div>
            <div className="text-8xl font-light text-neutral-900 mb-6">
              {minutes}:{seconds.toString().padStart(2, '0')}
            </div>
            {!isSetInProgress && onStartSet && (
              <Button variant="default" onClick={onStartSet} size="lg" className="w-full">
                <Play className="h-5 w-5" />
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            <div className="text-xl text-neutral-600">
              Set {currentSet.set_number} of {totalSets}
            </div>
            
            {/* Editable metadata fields */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm text-neutral-600 mb-3">Reps</label>
                <Input
                  type="number"
                  value={editedReps ?? ''}
                  onChange={(e) => setEditedReps(e.target.value ? parseInt(e.target.value) : undefined)}
                  className="text-center text-5xl font-semibold h-20"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm text-neutral-600 mb-3">Weight (kg)</label>
                <Input
                  type="number"
                  value={editedWeight ?? ''}
                  onChange={(e) => setEditedWeight(e.target.value ? parseFloat(e.target.value) : undefined)}
                  className="text-center text-5xl font-semibold h-20"
                  min="0"
                  step="0.5"
                />
              </div>
              
              {/* Save Changes button */}
              {onSaveMetadata && hasChanges && (
                <Button
                  onClick={handleSaveMetadata}
                  disabled={isSavingMetadata}
                  className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSavingMetadata ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3 w-full max-w-md">
        {!isNavigatingHistory ? (
          <>
            <Button
              variant="default"
              onClick={() =>
                onComplete({
                  actual_reps: editedReps,
                  actual_weight: editedWeight,
                  actual_rpe: editedRpe,
                  actual_duration_seconds: editedDuration,
                })
              }
              size="lg"
              className="w-full bg-neutral-900 text-white hover:bg-neutral-800"
            >
              Done
            </Button>
            {onSkip && (
              <Button 
                variant="ghost" 
                onClick={onSkip} 
                size="sm"
                className="w-full text-neutral-500 hover:text-neutral-700"
              >
                Skip Set
              </Button>
            )}
          </>
        ) : (
          <div className="flex items-center justify-between gap-4">
            {onPrevious && (
              <Button 
                variant="outline" 
                onClick={onPrevious} 
                size="sm"
                className="flex-1"
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Previous
              </Button>
            )}
            {onNext && (
              <Button 
                variant="outline" 
                onClick={onNext} 
                size="sm"
                className="flex-1"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

